import { useState } from "react";
import { Play, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface YouTubePlayerProps {
  url: string;
  title?: string;
  className?: string;
}

const YouTubePlayer = ({ url, title = "Vidéo", className = "" }: YouTubePlayerProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Extract YouTube video ID from various URL formats
  const getYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const videoId = getYouTubeVideoId(url);
  
  if (!videoId) {
    return (
      <div className={`aspect-video rounded-lg border border-destructive bg-destructive/10 flex items-center justify-center p-4 ${className}`}>
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-sm text-destructive font-medium">URL YouTube invalide</p>
          <p className="text-xs text-muted-foreground">
            Formats supportés :<br/>
            • https://www.youtube.com/watch?v=VIDEO_ID<br/>
            • https://youtu.be/VIDEO_ID
          </p>
        </div>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1`;
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  if (hasError) {
    return (
      <div className={`aspect-video rounded-lg border border-destructive bg-destructive/10 flex items-center justify-center p-4 ${className}`}>
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-sm text-destructive font-medium">Erreur de chargement</p>
          <p className="text-xs text-muted-foreground">
            La vidéo n'a pas pu être chargée
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`aspect-video rounded-lg overflow-hidden bg-black relative group ${className}`}>
      {!isLoaded ? (
        <>
          <img
            src={thumbnailUrl}
            alt={`Miniature de ${title}`}
            className="w-full h-full object-cover"
            onError={() => setHasError(true)}
          />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/20 transition-colors">
            <Button
              onClick={() => setIsLoaded(true)}
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white rounded-full h-16 w-16 p-0 shadow-lg hover:scale-105 transition-all"
            >
              <Play className="h-6 w-6 ml-1" fill="currentColor" />
            </Button>
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-medium text-sm bg-black/50 px-3 py-1 rounded backdrop-blur-sm">
              {title}
            </h3>
          </div>
        </>
      ) : (
        <iframe
          src={embedUrl}
          title={title}
          className="w-full h-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      )}
    </div>
  );
};

export default YouTubePlayer;